import { Box, Button, IconButton, Stack, Typography, Paper, Badge } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import CustomDivider from '../../shared/utils/CustomDivider';
import { NormalContainerWithHeader } from '../../shared/components/student/NormalComponent';
import { postRaisedTicket } from '../../assets/nodeApi/postApi/postAdminApi';
import { Grid_Gap, Input_Width } from '../../shared/constants';
import { useToaster } from '../../shared/utils/ToasterProvider';
import { queryClient } from '../../assets/nodeApi/reactQueryClient';
import { useTicketDropdownOptions } from '../admin/ticket/TicketOption';
import { TextFormField } from '../../shared/components/formfields/TextFormField';
import { AutoCompleteField } from '../../shared/components/formfields/AutoCompleteField';
import { useGetTicketsChatsById, useMyRaisedTicket } from '../../assets/nodeApi/getApi/getCommonApi';
import TableFilterTemplate from '../../shared/utils/TableFilterTemplate';
import { DropzoneUploadFormFileMultiple } from '../../shared/components/formfields/DropzoneUploadFormFileMultiple';
import { useDialog } from '../../shared/hook/useDialog';
import MainDialogBox from '../../shared/components/Dialog/MainDialogBox';
import { getFileUrl } from '../../shared/utils/getFileUrl';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { socket } from '../../socket';
import { postTicketChat } from '../../assets/nodeApi/postApi/postCommonApi';
import useAuthStore from '../../store/useAuthStore';
import ChatIcon from '@mui/icons-material/Chat';
import { patchMarkAllRead } from '../../assets/nodeApi/patchApi/patchCommonApi';
import ChatModule from '../../shared/components/ChatModule';


const RaiseTicketForm = () => {
  const dropzoneSetFilesRef = useRef(null);
  const [selectedTicket, setSelectedTicket] = useState({});
  const [selectedTicketForChat, setSelectedTicketForChat] = useState({});
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [raisedTicketCounts, setRaisedTicketCounts] = useState({});

  const validationSchema = Yup.object().shape({
    category: Yup.string().required('Category is required'),
    priority: Yup.string().required('Priority is required'),
    description: Yup.string().required('Description is required'),
    attachments: Yup.array().min(1, 'At least one attachment is required'),
  });

  const initialValues = {
    category: '',
    priority: '',
    description: '',
    attachments: [],
  };
  const { user } = useAuthStore();
  const { data: raisedTicketList, isLoading } = useMyRaisedTicket();
  const { showToaster } = useToaster();
  const { priorityLevel, ticketCategory } = useTicketDropdownOptions();

  const { data: chatData, isLoading: isChatLoading } = useGetTicketsChatsById(selectedTicketForChat?.id);
  
  const { open, handleClickOpen, handleClose } = useDialog();

  const mutationAdd = useMutation({
    mutationFn: (formData) => postRaisedTicket(formData),
  });

  const mutationSendChat = useMutation({
    mutationFn: (formData) => postTicketChat(formData),
  });

  // useEffect(() => {
  //   if (!selectedTicketForChat?.id) return;

  //   // Join the room for this ticket
  //   socket.emit("joinTicket", {
  //     ticketId: selectedTicketForChat.id,
  //     userId: user?.id,
  //   });

  //   // Load existing messages from API
  //   if (chatData?.data?.length) { 
  //     setMessages(chatData.data);
  //   }

  //   // Listen for new messages
  //   const handleNewMessage = (newMsg) => {
  //     // Only append if it’s for the current ticket
  //     if (newMsg.ticketId === selectedTicketForChat.id) {
  //       setMessages((prev) => [...prev, newMsg]);
  //     }
  //   };

  //   socket.on("newMessage", handleNewMessage);

  //   return () => {
  //     socket.emit("leaveTicket", {
  //       ticketId: selectedTicketForChat.id,
  //       userId: user?.id,
  //     });
  //     socket.off("newMessage", handleNewMessage);
  //   };
  // }, [selectedTicketForChat, chatData, user]);


  useEffect(() => {
    const ticketId = selectedTicketForChat?.id;
    const userId = user?.id;
    if (!ticketId || !userId) return;

    // Join ticket-specific room
    socket.emit("joinTicket", { ticketId, userId });
    console.log(`🟢 Joined chat room for ticket_${ticketId}`);

    // Load messages from API
    if (chatData?.data?.length) setMessages(chatData.data);

    // Listen for new messages
    const handleNewMessage = (newMsg) => {
      if (newMsg.ticketId === ticketId) {
        setMessages((prev) => [...prev, newMsg]);
      }
    };
    socket.on("newMessage", handleNewMessage);

    return () => {
      console.log(`🚪 Leaving chat room for ticket_${ticketId}`);
      socket.emit("leaveTicket", { ticketId, userId });
      socket.off("newMessage", handleNewMessage);
    };
  }, [selectedTicketForChat?.id, user?.id, chatData]);


  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;

    const handleChatCountUpdate = (data) => {
      setRaisedTicketCounts((prev) => ({
        ...prev,
        [data.ticketId]: {
          unread: data.unreadCount,
          total: data.totalMessageCount,
        },
      }));
    };

    socket.on("chatCountUpdate", handleChatCountUpdate);

    return () => {
      socket.off("chatCountUpdate", handleChatCountUpdate);
    };
  }, [user?.id]);




  useEffect(() => {
    if (raisedTicketList?.tickets?.length) {
      const initialCounts = {};
      raisedTicketList.tickets.forEach((t) => {
        initialCounts[t.id] = {
          unread: t.messageCounts?.unread || 0,
          total: t.messageCounts?.total || 0,
        };
      });
      setRaisedTicketCounts(initialCounts);
    }
  }, [raisedTicketList]);

  // 🔹 Raise Ticket Form
  const handleSubmit = (values, { setSubmitting, resetForm, setFieldValue }) => {
    const formData = new FormData();
    formData.append('category', values.category);
    formData.append('priority', values.priority);
    formData.append('description', values.description);
    values.attachments?.forEach((item) => {
      formData.append('attachments', item.file || item);
    });

    mutationAdd.mutate(formData, {
      onSuccess: () => {
        showToaster('Success', 'Ticket raised successfully!', 'success');
        queryClient.invalidateQueries({ queryKey: ["tickets/my-raised"] });
        resetForm();
        setFieldValue("attachments", []);
        if (dropzoneSetFilesRef.current) dropzoneSetFilesRef.current([]);
        setSubmitting(false);
      },
      onError: (err) => {
        showToaster('Error', err?.response?.data?.error || 'Failed', 'error');
        setSubmitting(false);
      },
    });
  };

  // 🔹 Chat Submit Handler
  const handleSubmitChat = (values, { resetForm, setSubmitting }) => {
    const payload = {
      ticketId: selectedTicketForChat?.id,
      receiverId: 1,
      message: values.message,
      messageType: "text"
    };
    mutationSendChat.mutate(payload, {
      onSuccess: () => {
        // emit to socket immediately
        socket.emit("sendMessage", payload);
        resetForm();
        setSubmitting(false);
      },
      onError: (err) => {
        showToaster('Error', err?.response?.data?.error || 'Failed to send', 'error');
        setSubmitting(false);
      },
    });
  };

  const raisedTicketOption = raisedTicketList?.tickets?.map((item, index) => ({
    id: item.id,
    sr_no: index + 1,
    ...item,
  })) ?? [];

  const mutationMardAsRead = useMutation({
    mutationFn: (formData) => patchMarkAllRead(formData),
  });

  const handleViewChat = (row) => {
    setSelectedTicketForChat(row);
    setIsChatOpen(true);
    mutationMardAsRead.mutate({ ticketId: row?.id })
    setMessages([])
  };

  const handleViewTicket = (row) => {
    handleClickOpen();
    setSelectedTicket(row);
  };

  const raisedTicketColumns = [
    { field: 'sr_no', headerName: 'Sr No', flex: 1 },
    { field: 'ticketId', headerName: 'Ticket Id', flex: 1 },
    { field: 'category', headerName: 'Category', flex: 1 },
    { field: 'priority', headerName: 'Priority', flex: 1 },
    { field: 'description', headerName: 'Description', flex: 2 },
    { field: 'status', headerName: 'Status', flex: 1 },
    {
      field: "attachments",
      headerName: "Attachments",
      flex: 1,
      renderCell: (params) => (
        <IconButton onClick={() => handleViewTicket(params?.row)} size="small">
          <VisibilityIcon color="text.primary" />
        </IconButton>
      ),
    },
    {
      field: "chat",
      headerName: "Conversation",
      flex: 1,
      renderCell: (params) => (
        <IconButton
          // color="primary"
          onClick={() => handleViewChat(params.row)}
        >
          <Badge
            badgeContent={raisedTicketCounts[params.row.id]?.unread || 0}
            color="error"
          >
            <ChatIcon />
          </Badge>

        </IconButton>
      ),
    },
  ];

  return (
    <Box>

      <Box
        sx={{
          position: "relative",
          pointerEvents: isChatOpen ? "none" : "auto", // 🚫 disables interaction
          opacity: isChatOpen ? 0.4 : 1,              // 🌑 dim effect
          transition: "opacity 0.3s ease",
        }}
      >
        <CustomDivider />
        <NormalContainerWithHeader headerText="Raise Ticket">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ setFieldValue, resetForm, isSubmitting }) => (
              <Form>
                <Grid2 container spacing={Grid_Gap}>
                  <Grid2 size={Input_Width}>
                    <Field
                      name="priority"
                      component={AutoCompleteField}
                      options={priorityLevel.options}
                      label="Priority Level"
                    />
                  </Grid2>

                  <Grid2 size={Input_Width}>
                    <Field
                      name="category"
                      component={AutoCompleteField}
                      options={ticketCategory.options}
                      label="Ticket Category"
                    />
                  </Grid2>

                  <Grid2 size={Input_Width}>
                    <Field
                      name="description"
                      component={TextFormField}
                      label="Description"
                      multiline
                      rows={3}
                    />
                  </Grid2>

                  <Grid2 size={12}>
                    <Field
                      name="attachments"
                      component={DropzoneUploadFormFileMultiple}
                      label="Attachments"
                      multiple
                      onChange={(files) => setFieldValue('attachments', files)}
                      setFilesRef={dropzoneSetFilesRef}
                    />
                  </Grid2>
                </Grid2>

                <CustomDivider />
                <Grid2 container spacing={Grid_Gap} sx={{justifyContent:"end"}}>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <Button
                      variant="outlined"
                      type="button"
                      onClick={() => resetForm()}
                      fullWidth
                    >
                      Reset
                    </Button>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <Button
                      variant="contained"
                      type="submit"
                      fullWidth
                      disabled={isSubmitting || mutationAdd.isLoading}
                    >
                      {isSubmitting || mutationAdd.isLoading
                        ? 'Submitting...'
                        : 'Submit'}
                    </Button>
                  </Grid2>
                </Grid2>
              </Form>
            )}
          </Formik>
        </NormalContainerWithHeader>

        <CustomDivider />

        <TableFilterTemplate
          title="Raised Tickets"
          columns={raisedTicketColumns}
          rowsData={raisedTicketOption}
          isLoading={isLoading}
        />

      </Box>

      {/* ATTACHMENT DIALOG */}


      {/* ✅ CHAT PANEL */}
      {/* {isChatOpen && (
        <Box
          sx={{
            position: 'fixed',
            right: 0,
            top: 0,
            height: '100vh',
            width: '400px',
            bgcolor: '#fff',
            borderLeft: '1px solid #ddd',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ position: "relative", p: 2 }}>
            <IconButton
              sx={{
                position: "absolute",
                top: "20px",
                right: "5px",
                zIndex: 1000,
                bgcolor: "text.secondary",
                p: 0.1,
                borderRadius: "50%",
                boxShadow: 2,
                "&:hover": { backgroundColor: "text.secondary" },
              }}
              onClick={() => {
                setSelectedTicketForChat({});
                setIsChatOpen(false);
              }}
            >
              <CloseIcon sx={{ color: "#000", fontSize: "25px" }} />
            </IconButton>
          </Box>

          <Typography variant="h6" mb={2}>
            Chat for Ticket #{selectedTicketForChat?.ticketId}
          </Typography>

          <Paper sx={{ flexGrow: 1, p: 2, overflowY: 'auto', mb: 2 }}>
            {!isChatLoading && messages.map((msg, i) => {
              const isMyMsg = msg.senderId == user?.id;
              return (
                <Box
                  key={msg.id || i}
                  sx={{
                    mb: 1.5,
                    textAlign: isMyMsg ? 'right' : 'left',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ display: 'block' }}
                  >
                    {isMyMsg ? 'You' : msg.sender?.username || 'Unknown'} •{' '}
                    {new Date(msg.createdAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      display: 'inline-block',
                      bgcolor: isMyMsg ? 'blue.main' : 'grey.300',
                      color: isMyMsg ? 'white' : 'black',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      maxWidth: '80%',
                      wordBreak: 'break-word',
                    }}
                  >
                    {msg.message}
                  </Typography>
                </Box>
              );
            })}

            {
              isChatLoading && <Typography>chat loading..........</Typography>
            }
          </Paper>
          <Formik
            initialValues={{ message: '' }}
            validationSchema={Yup.object({ message: Yup.string().required() })}
            onSubmit={handleSubmitChat}
          >
            {() => (
              <Form>
                <Box display={"flex"} alignItems={"center"} gap={1}>

                  <Field
                    name="message"
                    component={TextFormField}
                    placeholder="Type message..."
                    fullWidth
                  />

                  <Button variant="contained" type="submit" disabled={mutationSendChat?.isPending} style={{ marginTop: "15px" }}>
                    {mutationSendChat.isPending ? "Sending" : "Send"}
                  </Button>


                </Box>
              </Form>
            )}
          </Formik>

          <Button onClick={() => setIsChatOpen(false)} sx={{ mt: 2 }}>
            Close
          </Button>
        </Box>
      )} */}

      {isChatOpen && (
        <ChatModule
          selectedTicket={selectedTicketForChat}
          onClose={() => {
            setSelectedTicketForChat({});
            setIsChatOpen(false);
          }}
          receiverId={1}
        />
      )}



      <MainDialogBox open={open} handleClose={handleClose} width={"50%"} heading={"Attachments List"}>
        <Grid2 container spacing={Grid_Gap}>
          {selectedTicket?.attachments?.map((item, index) => (
            <Grid2 key={index} size={12}>
              <img src={getFileUrl(item?.url)} width={"100%"} alt="attachment" />
            </Grid2>
          ))}
        </Grid2>
      </MainDialogBox>
    </Box>
  );
};

export default RaiseTicketForm;


